import { CurrencyISO } from '@/constants/currencies';
import { useNarrativeValuationOptions } from '../../../hooks/useNarrativeValuationOptions';
import { NarrativeSupportedValuationType } from '../../../utils';
import { useMemo } from 'react';
import useUser from '@/hooks/useUser';

type Props = {
  companyId: string;
};

export const useValuationSelectionFormData = ({ companyId }: Props) => {
  const { data: user } = useUser();
  const { data: valuationOptionsData } = useNarrativeValuationOptions({
    companyId,
  });
  const isNarrativeOnboardingComplete = Boolean(user.displayName);

  return useMemo(() => {
    const { company } = valuationOptionsData ?? {};
    const {
      valuationOptions,
      name: companyName,
      tickerSymbol,
      analysisValue,
      data,
    } = company ?? {};
    const { priceTargetAnalystCount } = analysisValue ?? {};
    const { currencyInfo } = data ?? {};
    const companyCurrencyISO = (
      currencyInfo as { trading_item_currency_iso: CurrencyISO }
    )?.trading_item_currency_iso;

    const savedValuationOption = valuationOptions?.find(
      (valuationOption) => valuationOption.valuation?.narrative?.isPrimary
    );
    const { type } = savedValuationOption ?? {};

    const valuationOptionsMap = Object.fromEntries(
      valuationOptions?.map((option) => [option.type, option] as const) ?? []
    ) as Partial<
      Record<
        NarrativeSupportedValuationType,
        NonNullable<typeof valuationOptions>[number]
      >
    >;

    const userDefinedValuationOption = valuationOptionsMap?.USER_DEFINED;
    const dcfValuationOption = valuationOptionsMap?.DCF;
    const analystsValuationOption = valuationOptionsMap?.ANALYSTS;

    return {
      savedValuationType: type,
      isNarrativeOnboardingComplete,

      companyName,
      tickerSymbol,
      priceTargetAnalystCount,
      companyCurrencyISO,
      userDefinedValuationOption,
      dcfValuationOption,
      analystsValuationOption,

      userDefinedNarrativeId:
        userDefinedValuationOption?.valuation?.narrative?.id,
    };
  }, [valuationOptionsData, isNarrativeOnboardingComplete]);
};
